import { 
  CheckCircle, 
  XCircle, 
  Archive, 
  Copy, 
  FileEdit, 
  Users, 
  FileDown,
  MoreHorizontal,
  Lock,
  Unlock
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { Game } from '../../types/api'

export interface GameOptionsDropdownProps {
  project: Game
  
  // Callbacks for actions
  onPublish?: (project: Game) => void | Promise<void>
  onUnpublish?: (project: Game) => void | Promise<void>
  onMakePrivate?: (project: Game) => void | Promise<void>
  onMakePublic?: (project: Game) => void | Promise<void>
  onArchive?: (project: Game) => void | Promise<void>
  onUnarchive?: (project: Game) => void | Promise<void>
  onDuplicate?: (project: Game) => void | Promise<void>
  onEdit?: (project: Game) => void | Promise<void>
  onTransfer?: (project: Game) => void | Promise<void>
  onExport?: (project: Game) => void | Promise<void>
  
  // State flags
  isActionInProgress?: boolean
  
  // Visual customization
  buttonClassName?: string
  dropdownClassName?: string
  size?: 'sm' | 'md' | 'lg'
  
  // External dropdown state control (optional)
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  
  // Stop propagation on click (for cards)
  stopPropagation?: boolean
}

const SIZE_CONFIG = {
  sm: {
    button: 'p-1.5',
    icon: 'w-4 h-4',
    dropdown: 'w-48',
  },
  md: {
    button: 'p-2',
    icon: 'w-5 h-5',
    dropdown: 'w-52',
  },
  lg: {
    button: 'p-2.5',
    icon: 'w-5 h-5',
    dropdown: 'w-56',
  },
}

export default function GameOptionsDropdown({
  project,
  onPublish,
  onUnpublish,
  onMakePrivate,
  onMakePublic,
  onArchive,
  onUnarchive,
  onDuplicate,
  onEdit,
  onTransfer,
  onExport,
  isActionInProgress = false,
  buttonClassName,
  dropdownClassName,
  size = 'md',
  isOpen: externalIsOpen,
  onOpenChange,
  stopPropagation = false,
}: GameOptionsDropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Use external state if provided, otherwise use internal
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
  const setIsOpen = onOpenChange || setInternalIsOpen
  
  const sizeConfig = SIZE_CONFIG[size]
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, setIsOpen])
  
  const handleButtonClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
    setIsOpen(!isOpen)
  }
  
  const handleAction = async (e: React.MouseEvent, action?: (project: Game) => void | Promise<void>) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
    setIsOpen(false)
    
    if (action) {
      await action(project)
    }
  }
  
  // Determine which options to show based on project state
  const isArchived = project.archived_at !== null
  const hasVersions = !!project.current_version_id
  const isPublished = project.status === 'active'
  const isInDevelopment = !hasVersions
  
  // Count available actions to determine if we should show the dropdown at all
  const availableActions = [
    !isArchived && hasVersions && (onPublish || onUnpublish),
    !isArchived && hasVersions && (onMakePrivate || onMakePublic),
    !isArchived && onArchive,
    isArchived && onUnarchive,
    onDuplicate,
    onEdit,
    onTransfer,
    onExport,
  ].filter(Boolean).length
  
  if (availableActions === 0) {
    return null
  }
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleButtonClick}
        className={
          buttonClassName ||
          `${sizeConfig.button} hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors`
        }
        title="More options"
        disabled={isActionInProgress}
      >
        <MoreHorizontal className={`${sizeConfig.icon} text-gray-600 dark:text-gray-300`} />
      </button>
      
      {isOpen && (
        <div
          className={
            dropdownClassName ||
            `absolute right-0 mt-2 ${sizeConfig.dropdown} bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 overflow-hidden`
          }
        >
          {/* Publish/Unpublish */}
          {(onPublish || onUnpublish) && (
            <>
              <button
                onClick={(e) => handleAction(e, isPublished ? onUnpublish : onPublish)}
                disabled={isActionInProgress}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPublished ? (
                  <>
                    <XCircle className="w-4 h-4" />
                    Unpublish Game
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Publish Game
                  </>
                )}
              </button>
            </>
          )}
          
          {/* Make Private / Make Public */}
          {(onMakePrivate || onMakePublic) && (
            <button
              onClick={(e) => handleAction(e, onMakePrivate || onMakePublic)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {onMakePrivate ? (
                <>
                  <Lock className="w-4 h-4" />
                  Make Private
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4" />
                  Make Public
                </>
              )}
            </button>
          )}
          
          {/* Edit Description - Always available */}
          {onEdit && (
            <button
              onClick={(e) => handleAction(e, onEdit)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileEdit className="w-4 h-4" />
              Edit Description
            </button>
          )}
          
          {/* Duplicate - Always available */}
          {onDuplicate && (
            <button
              onClick={(e) => handleAction(e, onDuplicate)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Copy className="w-4 h-4" />
              Make a Copy
            </button>
          )}
          
          {/* Transfer - Always available */}
          {onTransfer && (
            <button
              onClick={(e) => handleAction(e, onTransfer)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Users className="w-4 h-4" />
              Transfer Game
            </button>
          )}
          
          {/* Archive - Only for non-archived games */}
          {!isArchived && onArchive && (
            <button
              onClick={(e) => handleAction(e, onArchive)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Archive className="w-4 h-4" />
              Archive Game
            </button>
          )}
          
          {/* Unarchive - Only for archived games */}
          {isArchived && onUnarchive && (
            <button
              onClick={(e) => handleAction(e, onUnarchive)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Archive className="w-4 h-4" />
              Unarchive Game
            </button>
          )}
          
          {/* Export - Always available */}
          {onExport && (
            <button
              onClick={(e) => handleAction(e, onExport)}
              disabled={isActionInProgress}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileDown className="w-4 h-4" />
              Export Game
            </button>
          )}
        </div>
      )}
    </div>
  )
}

